import { useLocation } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

import hedvigLogo from '../images/logos/hedvig.png';
import varsamLogo from '../images/logos/varsam.png';
import taxiLogo from '../images/logos/taxi.png';
import assistansbolagetLogo from '../images/logos/assistansbolaget.png';

import {
  SelectFnolTemplateView,
  FnolRecievedView,
  ContactInformationView,
  ContactPartnerView,
  IndentifyCustomerView,
  QuestionsView,
  UploadFilesView,
  SelectFnolCategoryView,
  SendFNOLView,
} from '../views';

import {
  STEP_SELECT_FNOL_TEMPLATE,
  STEP_ANSWER_QUESTIONS,
  STEP_CONTACT_INFORMATION,
  STEP_CONTACT_PARTNER,
  STEP_SELECT_FNOL_CATEGORY,
  STEP_FNOL_RECIEVED,
  STEP_IDENTIFY_CUSTOMER,
  STEP_UPLOAD_FILE,
  STEP_SEND_FNOL,
} from '../CONSTANTS';

import { useFnol } from '../context';
import { ProgressBar } from '../components';
import { useTranslation } from 'react-i18next';

const useQueryParam = () => {
  return new URLSearchParams(useLocation().search);
};

const PageContainer = ({ children }) => {
  const { step } = useFnol();
  const { t } = useTranslation();

  const theme = useQueryParam().get('theme') || 'default';

  const themes = {
    default: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#2e2e2e',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #2e2e2e',
          borderLeft: '0.0625rem solid #2e2e2e',
          borderRight: '0.0625rem solid #2e2e2e',
          borderTop: '0.0625rem solid #2e2e2e',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#151515',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#4c4b4b',
            borderColor: '#151515',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#2e2e2e',
            borderColor: '#2e2e2e',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#2e2e2e',
          borderBottom: '0.0625rem solid #2e2e2e',
          borderLeft: '0.0625rem solid #2e2e2e',
          borderRight: '0.0625rem solid #2e2e2e',
          borderTop: '0.0625rem solid #2e2e2e',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            fontColor: '#2e2e2e',
            borderColor: '#2e2e2e',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#e2e2e2',
            backgroundColor: 'transparent',
            borderColor: '#2e2e2e',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#d3e5f1',
        borderBottom: '0.125rem solid #2e2e2e',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#d3e5f1',
          borderColor: '#2e2e2e',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #2e2e2e',
        borderLeft: '0.0625rem solid #2e2e2e',
        borderRight: '0.0625rem solid #2e2e2e',
        borderTop: '0.0625rem solid #2e2e2e',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#2e2e2e',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#2e2e2e',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#2e2e2e',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #1ad8a9',
          fontColor: '#1ad8a9',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #dfecf4',
          fontColor: '#dfecf4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#00c2ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#1ad8a9',
          },
        },
      },
    },
    lin: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#20d8a9',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #20d8a9',
          borderLeft: '0.0625rem solid #20d8a9',
          borderRight: '0.0625rem solid #20d8a9',
          borderTop: '0.0625rem solid #20d8a9',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#1ecb9f',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#3ae2b7',
            borderColor: 'none',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#20d8a9',
            borderColor: '#20d8a9',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#00c2ff',
          borderBottom: '0.0625rem solid #00c2ff',
          borderLeft: '0.0625rem solid #00c2ff',
          borderRight: '0.0625rem solid #00c2ff',
          borderTop: '0.0625rem solid #00c2ff',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            backgroundColor: 'transparent',
            fontColor: '#3ae2b7',
            borderColor: '#3ae2b7',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#00c2ff',
            backgroundColor: 'transparent',
            borderColor: '#00c2ff',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#d3e5f1',
        borderBottom: '0.125rem solid #6e84be',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#d3e5f1',
          borderColor: '#6e84be',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #00c2ff',
        borderLeft: '0.0625rem solid #00c2ff',
        borderRight: '0.0625rem solid #00c2ff',
        borderTop: '0.0625rem solid #00c2ff',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#00c2ff',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#00c2ff',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#00c2ff',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #1ad8a9',
          fontColor: '#1ad8a9',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #dfecf4',
          fontColor: '#dfecf4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#00c2ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#00c2ff',
          },
        },
      },
    },
    fair: {
      baseFont: {
        fontFamily: 'Inter',
        fontWeight: '400',
        fontColor: '#333',
        fontSize: '16px',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Figtree',
        fontWeight: '300',
        fontColor: '#333',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Figtree',
        fontWeight: '300',
        fontColor: '#333',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Figtree',
        fontWeight: '300',
        fontColor: '#333',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Figtree',
        fontWeight: '300',
        fontColor: '#333',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Inter',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#005055',
          fontColor: '#fff',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          borderRadius: '999px',
          padding: '0 1rem',

          hover: {
            fontColor: '#fff',
            backgroundColor: '#3ae2b7',
            borderColor: 'none',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#005055',
            borderColor: 'none',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#005055',
          borderBottom: '1px solid #005055',
          borderLeft: '1px solid #005055',
          borderRight: '1px solid #005055',
          borderTop: '1px solid #005055',
          borderRadius: '999px',
          padding: '0.75rem 1rem',
          hover: {
            backgroundColor: 'transparent',
            fontColor: '#005055',
            borderColor: '#005055',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#005055',
            backgroundColor: 'transparent',
            borderColor: '#005055',
          },
        },
      },
      input: {
        fontColor: '#121212',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #707070',
        borderLeft: '0.0625rem solid #707070',
        borderRight: '0.0625rem solid #707070',
        borderTop: '0.0625rem solid #707070',
        borderRadius: '0.625rem',
        padding: '0.6rem 0.875rem',
        focus: {
          backgroundColor: '#fff',
          borderColor: '#0e7e78',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#121212',
          fontSize: '18px',
          fontWeight: '400',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '1px solid #005055',
        borderLeft: '1px solid #005055',
        borderRight: '1px solid #005055',
        borderTop: '1px solid #005055',
        borderRadius: '0.625rem',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#005055',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#005055',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: '0.1875rem solid #0e7e7a',
          fontColor: '#fafafa',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#0e7e7a',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #0E7E7A',
          fontColor: '#0e7e7a',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #707070',
          fontColor: '#707070',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #005055',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #005055',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#005055',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#0E7E7A',
          },
        },
      },
    },
    hedvig: {
      logo: {
        src: hedvigLogo,
        height: '25px',
        width: '88px',
        padding: '56px',
      },
      baseFont: {
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontColor: '#707070',
        fontSize: '18px',
      },
      errorMessage: {
        fontColor: '#ff513a',
      },
      h1: {
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontColor: '#121212',
        fontSize: '18px',
      },
      h2: {
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontColor: '#121212',
        fontSize: '18px',
      },
      h3: {
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontColor: '#121212',
        fontSize: '18px',
      },
      h4: {
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontColor: '#121212',
        fontSize: '18px',
      },
      button: {
        align: 'stretch',
        fontFamily: 'HedvigLettersSans',
        fontWeight: '400',
        fontSize: '18px',
        primary: {
          backgroundColor: '#121212',
          fontColor: '#fafafa',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          borderRadius: '12px',
          padding: '16px 32px',
          hover: {
            fontColor: '#fafafa',
            backgroundColor: '#303030',
            borderColor: 'none',
          },
          disabled: {
            opacity: 1,
            fontColor: '#cfcfcf',
            backgroundColor: '#f0f0f0',
            borderColor: 'none',
          },
        },
        secondary: {
          backgroundColor: '#f0f0f0',
          fontColor: '#121212',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          borderRadius: '12px',
          padding: '16px 32px',
          hover: {
            backgroundColor: '#e7e7e7',
            fontColor: '#121212',
            borderColor: 'none',
          },
          disabled: {
            opacity: 1,
            fontColor: '#f0f0f0',
            backgroundColor: '#cfcfcf',
            borderColor: 'none',
          },
        },
      },
      input: {
        fontColor: '#121212',
        fontSize: '18px',
        fontWeight: '400',
        backgroundColor: '#f0f0f0',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        borderRadius: '12px',
        padding: '16px',
        focus: {
          borderColor: '',
        },
        label: {
          fontColor: '#121212',
          fontSize: '18px',
          fontWeight: '400',
        },
      },
      listItem: {
        backgroundColor: '#f0f0f0',
        borderBottom: '2px solid #f0f0f0',
        borderLeft: '2px solid #f0f0f0',
        borderRight: '2px solid #f0f0f0',
        borderTop: '2px solid #f0f0f0',
        borderRadius: '12px',
        fontSize: '18px',
        fontColor: '#707070',
        headerFontSize: '18px',
        headerFontWeight: '400',
        headerFontColor: '#121212',
        padding: '14px 16px 16px 16px',
        hover: {
          backgroundColor: '#eaeaea',
          borderColor: '#1212121D',
          boxShadow: 'none',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '40px',
          border: 'none',
          fontColor: '#fafafa',
          fontSize: '18px',
          fontWeight: '400',
          backgroundColor: '#121212',
        },
        completed: {
          diameter: '40px',
          border: 'none',
          fontColor: '#fafafa',
          fontSize: '18px',
          fontWeight: '400',
          backgroundColor: '#24cc5c',
        },
        waiting: {
          diameter: '40px',
          border: '2px solid #cfcfcf',
          fontColor: '#cfcfcf',
          fontSize: '18px',
          fontWeight: '400',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '18px',
        fontWeight: '400',
        align: 'stretch',
        padding: '15px 14px 17px 14px',
        gap: '8px',
        borderRadius: '12px',
        wrapperBackgroundColor: '#f0f0f0',
        outerCircleDiameter: '24px',
        outerCircleBorder: '2px solid #cfcfcf',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '8px solid #24cc5c',
          innerCircleBackgroundDiameter: '0',
          innerCircleBackgroundColor: 'transparent',
        },
      },
      fileDropArea: {
        backgroundColor: '#f0f0f0',
        border: '2px dashed #1212121D',
        borderRadius: '12px',
        iconColor: '#121212',
        fontColor: '#707070',
        fontSize: '14px',
        file: {
          backgroundColor: '#f0f0f0',
          borderRadius: '12px',
          padding: '16px',
          active: {
            color: '#24CC5C',
          },
        },
      },
    },
    circleK: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#da291c',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #da291c',
          borderLeft: '0.0625rem solid #da291c',
          borderRight: '0.0625rem solid #da291c',
          borderTop: '0.0625rem solid #da291c',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#ed8b00',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#ed8b00',
            borderColor: '#ed8b00',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#cfcfcf',
            backgroundColor: '#da291c',
            borderColor: '#da291c',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#ed8b00',
          borderBottom: '0.0625rem solid #ed8b00',
          borderLeft: '0.0625rem solid #ed8b00',
          borderRight: '0.0625rem solid #ed8b00',
          borderTop: '0.0625rem solid #ed8b00',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            fontColor: '#fff',
            borderColor: 'none',
            backgroundColor: '#ed8b00',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#ed8b00',
            backgroundColor: 'transparent',
            borderColor: '#ed8b00',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #b1b1b1',
        borderLeft: '0.125rem solid #b1b1b1',
        borderRight: '0.125rem solid #b1b1b1',
        borderTop: '0.125rem solid #b1b1b1',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#ededed',
          borderColor: '#b1b1b1',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #da291c',
        borderLeft: '0.0625rem solid #da291c',
        borderRight: '0.0625rem solid #da291c',
        borderTop: '0.0625rem solid #da291c',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#da291c',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#da291c',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#da291c',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #ed8b00',
          fontColor: '#ed8b00',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #dfecf4',
          fontColor: '#dfecf4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#00c2ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#ed8b00',
          },
        },
      },
    },
    taxi: {
      logo: {
        src: taxiLogo,
        width: '25%',
        padding: '0.5rem',
      },
      baseFont: {
        fontFamily: 'NotoSans',
        fontWeight: '400',
        fontColor: '#000000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'NotoSans',
        fontWeight: '700',
        fontColor: '#32346a',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'NotoSans',
        fontWeight: '700',
        fontColor: '#32346a',
        fontSize: '1.6rem',
      },
      h3: {
        fontFamily: 'NotoSans',
        fontWeight: '700',
        fontColor: '#32346a',
        fontSize: '1.5rem',
      },
      h4: {
        fontFamily: 'NotoSans',
        fontWeight: '700',
        fontColor: '#32346a',
        fontSize: '1.4rem',
      },
      button: {
        align: 'end',
        fontFamily: 'NotoSans',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#da291c',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #da291c',
          borderLeft: '0.0625rem solid #da291c',
          borderRight: '0.0625rem solid #da291c',
          borderTop: '0.0625rem solid #da291c',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#bc2015',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#bc2015',
            borderColor: '#bc2015',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#f29b95',
            borderColor: '#f29b95',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#da291c',
          borderBottom: '0.0625rem solid #da291c',
          borderLeft: '0.0625rem solid #da291c',
          borderRight: '0.0625rem solid #da291c',
          borderTop: '0.0625rem solid #da291c',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            fontColor: '#bc2015',
            borderColor: '#bc2015',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#f29b95',
            backgroundColor: 'transparent',
            borderColor: '#f29b95',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #32346a',
        borderLeft: '0.125rem solid #32346a',
        borderRight: '0.125rem solid #32346a',
        borderTop: '0.125rem solid #32346a',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#f8f6f6',
          borderColor: '##32346a',
          borderWidth: '0.125rem',
        },
        label: {
          fontWeight: '600',
          fontColor: '#32346a',
          fontSize: '1rem',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #32346a',
        borderLeft: '0.0625rem solid #32346a',
        borderRight: '0.0625rem solid #32346a',
        borderTop: '0.0625rem solid #32346a',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#32346a',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#32346a',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#df492c',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #32346a',
          fontColor: '#32346a',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #c4c4c4',
          fontColor: '#c4c4c4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#1481ba',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#19b14b',
          },
        },
      },
    },
    hajperf: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#00854a',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #00854a',
          borderLeft: '0.0625rem solid #00854a',
          borderRight: '0.0625rem solid #00854a',
          borderTop: '0.0625rem solid #00854a',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#27773a',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#27773a',
            borderColor: '#27773a',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#00854a',
            borderColor: '#00854a',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#19b14b',
          borderBottom: '0.0625rem solid #19b14b',
          borderLeft: '0.0625rem solid #19b14b',
          borderRight: '0.0625rem solid #19b14b',
          borderTop: '0.0625rem solid #19b14b',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            fontColor: '#00854a',
            borderColor: '#00854a',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#19b14b',
            backgroundColor: 'transparent',
            borderColor: '#19b14b',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #00854a',
        borderLeft: '0.125rem solid #00854a',
        borderRight: '0.125rem solid #00854a',
        borderTop: '0.125rem solid #00854a',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#edf6f2',
          borderColor: '#00854a',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #00854a',
        borderLeft: '0.0625rem solid #00854a',
        borderRight: '0.0625rem solid #00854a',
        borderTop: '0.0625rem solid #00854a',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#00854a',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#00854a',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#00854a',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #00854a',
          fontColor: '#00854a',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #c4c4c4',
          fontColor: '#c4c4c4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#00c2ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#00854a',
          },
        },
      },
    },
    easypeasy: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#000',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#000',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',
        primary: {
          backgroundColor: '#19b14b',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #19b14b',
          borderLeft: '0.0625rem solid #19b14b',
          borderRight: '0.0625rem solid #19b14b',
          borderTop: '0.0625rem solid #19b14b',
          borderRadius: '0',
          padding: '1.2rem 5.625rem 1.2rem 0.9375rem',
          secondaryBackgroundColor: '#396',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#396',
            borderColor: '#396',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#19b14b',
            borderColor: '#19b14b',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#19b14b',
          borderBottom: '0.0625rem solid #19b14b',
          borderLeft: '0.0625rem solid #19b14b',
          borderRight: '0.0625rem solid #19b14b',
          borderTop: '0.0625rem solid #19b14b',
          borderRadius: '0',
          padding: '1.2rem 1rem',
          hover: {
            fontColor: '#19b14b',
            borderColor: '#19b14b',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#19b14b',
            backgroundColor: 'transparent',
            borderColor: '#19b14b',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #19b14b',
        borderLeft: '0.125rem solid #19b14b',
        borderRight: '0.125rem solid #19b14b',
        borderTop: '0.125rem solid #19b14b',
        borderRadius: '0',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#ededed',
          borderColor: '#19b14b',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #19b14b',
        borderLeft: '0.0625rem solid #19b14b',
        borderRight: '0.0625rem solid #19b14b',
        borderTop: '0.0625rem solid #19b14b',
        borderRadius: '0',
        fontSize: '1rem',
        fontColor: '#333333',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#19b14b',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#19b14b',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#19b14b',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #19b14b',
          fontColor: '#19b14b',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #c4c4c4',
          fontColor: '#c4c4c4',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #6e84be',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #6e84be',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#00c2ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#19b14b',
          },
        },
      },
    },
    varsam: {
      logo: {
        src: varsamLogo,
        width: '50%',
        padding: '1.875rem',
      },
      baseFont: {
        fontFamily: 'Arial',
        fontWeight: '400',
        fontColor: '#010409',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Arial',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '1.2rem',
      },
      h2: {
        fontFamily: 'Arial',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '1.2rem',
      },
      h3: {
        fontFamily: 'Arial',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '1.2rem',
      },
      h4: {
        fontFamily: 'Arial',
        fontWeight: '700',
        fontColor: '#353d53',
        fontSize: '1.1rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Arial',
        fontWeight: '400',
        fontSize: '16px',
        padding: '0.125rem 2rem',
        primary: {
          backgroundColor: '##f5f5f5',
          fontColor: '#666',
          borderBottom: '0.0625rem solid #707070',
          borderLeft: '0.0625rem solid #707070',
          borderRight: '0.0625rem solid #707070',
          borderTop: '0.0625rem solid #707070',
          borderRadius: '0.188rem',
          padding: '0.625rem 2rem',
          height: 'max-content',
          hover: {
            backgroundColor: '#fff',
            fontColor: '#28a7de',
            borderColor: '#28a7de',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#707070',
            borderColor: '#707070',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#707070',
          borderBottom: '0.0625rem solid #707070',
          borderLeft: '0.0625rem solid #707070',
          borderRight: '0.0625rem solid #707070',
          borderTop: '0.0625rem solid #707070',
          borderRadius: '0.188rem',
          height: 'max-content',
          padding: '0.625rem 2rem',
          hover: {
            backgroundColor: 'transparent',
            fontColor: '#28a7de',
            borderColor: '#28a7de',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#525252',
            backgroundColor: 'transparent',
            borderColor: '#525252',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1rem',
        fontWeight: '300',
        backgroundColor: '#fefefe',
        borderBottom: '0.125rem solid #ccc',
        borderLeft: '0.125rem solid #ccc',
        borderRight: '0.125rem solid #ccc',
        borderTop: '0.125rem solid #ccc',
        borderRadius: '0',
        padding: '1rem',
        focus: {
          backgroundColor: '#fffddc',
          borderColor: '#faf395',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #8e2a88',
        borderLeft: '0.0625rem solid #8e2a88',
        borderRight: '0.0625rem solid #8e2a88',
        borderTop: '0.0625rem solid #8e2a88',
        borderRadius: '0.188rem',
        fontSize: '1rem',
        fontColor: '#010409',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#010409',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#df1189',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '40px',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.125rem',
          fontWeight: '400',
          backgroundColor: '#e70ee0',
        },
        completed: {
          diameter: '40px',
          border: '#8e2a88',
          fontColor: '#fff',
          fontSize: '1.125rem',
          fontWeight: '400',
          backgroundColor: '#8e2a88',
        },
        waiting: {
          diameter: '40px',
          border: '0.1875rem solid #cfcfcf',
          fontColor: '#cfcfcf',
          fontSize: '18px',
          fontWeight: '400',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '0.25rem',
        borderRadius: '0.5rem',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #ccc',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #2d75ff',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#2d75ff',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125rem dashed #eeeeee',
        borderRadius: '0.125rem',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#00c2ff',
          },
        },
      },
    },
    balder: {
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '##333',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '##333',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '##333',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '##333',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '##333',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',

        primary: {
          backgroundColor: '#369696',
          fontColor: '#fff',
          borderBottom: '0.0625rem solid #369696',
          borderLeft: '0.0625rem solid #369696',
          borderRight: '0.0625rem solid #369696',
          borderTop: '0.0625rem solid #369696',
          borderRadius: '10px',
          padding: '0.8rem 1.6rem',
          hover: {
            fontColor: '#fff',
            backgroundColor: '#44bbbb',
            borderColor: '#44bbbb',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#fff',
            backgroundColor: '#369696',
            borderColor: '#369696',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#004766',
          borderBottom: '0.0625rem solid #004766',
          borderLeft: '0.0625rem solid #004766',
          borderRight: '0.0625rem solid #004766',
          borderTop: '0.0625rem solid #004766',
          borderRadius: '0.625rem',
          padding: '0.8rem 1.6rem',
          hover: {
            fontColor: '#008fcc',
            borderColor: '#008fcc',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#85a1ad',
            backgroundColor: 'transparent',
            borderColor: '#85a1ad',
          },
        },
      },
      input: {
        fontColor: '#000',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #3d768f',
        borderLeft: '0.125rem solid #3d768f',
        borderRight: '0.125rem solid #3d768f',
        borderTop: '0.125rem solid #3d768f',
        borderRadius: '0.625rem',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#faf9f3',
          borderColor: '#3d768f',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#353d53',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #526e7a',
        borderLeft: '0.0625rem solid #526e7a',
        borderRight: '0.0625rem solid #526e7a',
        borderTop: '0.0625rem solid #526e7a',
        borderRadius: '0.625rem',
        fontSize: '1rem',
        fontColor: '#526e7a',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#003246',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#70a9c2',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#369696',
        },
        completed: {
          diameter: '2rem',
          border: '0.1875rem solid #004766',
          fontColor: '#fff',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#004766',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #f3f0e6',
          fontColor: '#f3f0e6',
          fontSize: '1.3rem',
          fontWeight: '700',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #003246',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #003246',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#369696',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#00854a',
          },
        },
      },
    },
    assistansbolaget: {
      logo: {
        src: assistansbolagetLogo,
        width: '50%',
        padding: '1.875rem',
      },
      baseFont: {
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontColor: '#4f555c',
        fontSize: '1rem',
      },
      errorMessage: {
        fontColor: 'red',
      },
      h1: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#4f555c',
        fontSize: '4rem',
      },
      h2: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#4f555c',
        fontSize: '2.5rem',
      },
      h3: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#4f555c',
        fontSize: '2rem',
      },
      h4: {
        fontFamily: 'Poppins',
        fontWeight: '700',
        fontColor: '#4f555c',
        fontSize: '1.5rem',
      },
      button: {
        align: 'end',
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '16px',

        primary: {
          backgroundColor: '#fad4a3',
          fontColor: '#000',
          borderBottom: '0.0625rem solid #fad4a3',
          borderLeft: '0.0625rem solid #fad4a3',
          borderRight: '0.0625rem solid #fad4a3',
          borderTop: '0.0625rem solid #fad4a3',
          borderRadius: '10px',
          padding: '0.8rem 1.6rem',
          hover: {
            fontColor: '#000',
            backgroundColor: '#fbeac7',
            borderColor: '#fce3bd',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#000',
            backgroundColor: '#fad4a3',
            borderColor: '#fad4a3',
          },
        },
        secondary: {
          backgroundColor: 'transparent',
          fontColor: '#223546',
          borderBottom: '0.0625rem solid #223546',
          borderLeft: '0.0625rem solid #223546',
          borderRight: '0.0625rem solid #223546',
          borderTop: '0.0625rem solid #223546',
          borderRadius: '0.625rem',
          padding: '0.8rem 1.6rem',
          hover: {
            fontColor: '#fad4a3',
            borderColor: '#fad4a3',
            backgroundColor: '#fff',
          },
          disabled: {
            opacity: 0.4,
            fontColor: '#223546',
            backgroundColor: 'transparent',
            borderColor: '#223546',
          },
        },
      },
      input: {
        fontColor: '#181818',
        fontSize: '1.25rem',
        fontWeight: '300',
        backgroundColor: '#fff',
        borderBottom: '0.125rem solid #181818',
        borderLeft: '0.125rem solid #181818',
        borderRight: '0.125rem solid #181818',
        borderTop: '0.125rem solid #181818',
        borderRadius: '0.625rem',
        padding: '0.563rem 0.875rem',
        focus: {
          backgroundColor: '#faf9f3',
          borderColor: '#000',
          borderWidth: '0.125rem',
        },
        label: {
          fontColor: '#000',
          fontSize: '1rem',
          fontWeight: '500',
        },
      },
      listItem: {
        backgroundColor: '#fff',
        borderBottom: '0.0625rem solid #000',
        borderLeft: '0.0625rem solid #000',
        borderRight: '0.0625rem solid #000',
        borderTop: '0.0625rem solid #000',
        borderRadius: '0.625rem',
        fontSize: '1rem',
        fontColor: '#4f555c',
        headerFontSize: '1.25rem',
        headerFontWeight: '700',
        headerFontColor: '#003246',
        padding: '0.5rem',
        hover: {
          backgroundColor: '#fff',
          borderColor: '#5382c1',
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0.125rem 0.62rem',
        },
        selected: {
          fontColor: '',
          backgroundColor: '',
        },
      },
      progressBar: {
        active: {
          diameter: '2rem',
          border: '0.1875rem solid #223546',
          fontColor: '#fad4a3',
          fontWeight: '500',
          backgroundColor: '#223546',
        },
        completed: {
          diameter: '2rem',
          border: 'none',
          fontColor: '#fad4a3',
          fontWeight: '500',
          backgroundColor: '#6b8898',
        },
        waiting: {
          diameter: '2rem',
          border: '0.1875rem solid #fad4a3',
          fontColor: '#fad4a3',
          fontWeight: '500',
          backgroundColor: '#fff',
        },
      },
      radioButton: {
        fontSize: '1rem',
        fontWeight: '400',
        align: 'start',
        padding: '0',
        gap: '4px',
        borderRadius: '8px',
        wrapperBackgroundColor: 'transparent',
        outerCircleDiameter: '1rem',
        outerCircleBorder: '0.125rem solid #4f555c',
        innerCircleBackgroundDiameter: '0',
        innerCircleBackgroundColor: 'transparent',
        selected: {
          outerCircleBorder: '0.125rem solid #4f555c',
          innerCircleBackgroundDiameter: '0.5rem',
          innerCircleBackgroundColor: '#5382c1',
        },
      },
      fileDropArea: {
        backgroundColor: '#fafafa',
        border: '0.125px dashed #eeeeee',
        borderRadius: '0.125px',
        iconColor: '#bdbdbd',
        fontColor: '#bdbdbd',
        fontSize: '1rem',
        file: {
          backgroundColor: '#f8f9fb',
          borderRadius: '0',
          padding: '0.438rem 0.25rem',
          active: {
            color: '#00854a',
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={themes[theme]}>
      <Container theme={themes[theme]}>
        <FontContainer>
          {children}
          {themes[theme].logo && (
            <LogoWrapper>
              <img src={themes[theme].logo.src} alt='' />
            </LogoWrapper>
          )}
          <h2>{t('fnolDescription.header')}</h2>
          <p>{step !== STEP_FNOL_RECIEVED && t('fnolDescription.text')}</p>
          <ProgressBar />
          {step === STEP_SELECT_FNOL_CATEGORY && (
            <SelectFnolCategoryView button={themes[theme].button} />
          )}
          {step === STEP_IDENTIFY_CUSTOMER && (
            <IndentifyCustomerView button={themes[theme].button} />
          )}
          {step === STEP_SELECT_FNOL_TEMPLATE && (
            <SelectFnolTemplateView button={themes[theme].button} />
          )}
          {step === STEP_ANSWER_QUESTIONS && (
            <QuestionsView button={themes[theme].button} />
          )}
          {step === STEP_UPLOAD_FILE && (
            <UploadFilesView button={themes[theme].button} />
          )}
          {step === STEP_CONTACT_PARTNER && (
            <ContactPartnerView button={themes[theme].button} />
          )}
          {step === STEP_CONTACT_INFORMATION && (
            <ContactInformationView button={themes[theme].button} />
          )}
          {step === STEP_SEND_FNOL && (
            <SendFNOLView button={themes[theme].button} />
          )}
          {step === STEP_FNOL_RECIEVED && <FnolRecievedView />}
        </FontContainer>
      </Container>
    </ThemeProvider>
  );
};

export default PageContainer;

const FontContainer = styled.div`
  font-family: ${(props) => props.theme.baseFont.fontFamily};
  color: ${(props) => props.theme.baseFont.fontColor};
  font-weight: ${(props) => props.theme.baseFont.fontWeight};
  font-size: ${(props) => props.theme.baseFont.fontSize};

  h2 {
    font-family: ${(props) => props.theme.h2.fontFamily};
    color: ${(props) => props.theme.h2.fontColor};
    font-weight: ${(props) => props.theme.h2.fontWeight};
    font-size: ${(props) => props.theme.h2.fontSize};
  }

  h3 {
    font-family: ${(props) => props.theme.h3.fontFamily};
    color: ${(props) => props.theme.h3.fontColor};
    font-weight: ${(props) => props.theme.h3.fontWeight};
    font-size: ${(props) => props.theme.h3.fontSize};
  }
`;

const Container = styled.div`
  margin: auto;
  width: 100%;
  max-width: 30rem;
  padding: 1rem;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: calc(${(props) => props.theme.logo.padding} - 1rem);
  padding-bottom: ${(props) => props.theme.logo.padding};

  img {
    height: ${(props) => props.theme.logo.height};
    width: ${(props) => props.theme.logo.width};
  }
`;
